import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";
import { statusOf } from "../testStatus";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Format an ISO timestamp for display; tolerant of null. */
function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString();
}

/* Duration between two ISO timestamps, e.g. "4.2s" or "1m 3s". */
function duration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function StatusBadge({ status }) {
  const s = statusOf(status);
  const Glyph = s.icon;
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        s.badge,
      ].join(" ")}
    >
      <Glyph className={["h-3.5 w-3.5", status === "running" ? "animate-spin" : ""].join(" ")} />
      {s.label}
    </span>
  );
}

/* Full-screen viewer for a single step's screenshot. */
function ScreenshotLightbox({ src, caption, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-full max-w-6xl flex-col overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-700">
        <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2 text-sm text-slate-200">
          <span className="truncate">{caption}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close screenshot"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black">
          <img src={src} alt={caption} className="mx-auto block h-auto max-w-full" />
        </div>
      </div>
    </div>
  );
}

/* One step row within an expanded run. */
function StepRow({ testCaseId, runkey, step, onOpenScreenshot }) {
  const s = statusOf(step.status);
  const Glyph = s.icon;
  const shotUrl = step.has_screenshot
    ? `${API_BASE_URL}testcases/${testCaseId}/runs/${runkey}/steps/${step.step_index}/screenshot`
    : null;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-slate-200 bg-card p-3">
      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
        {step.step_index}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={["inline-flex flex-none items-center justify-center rounded-full", s.solid].join(" ")}>
            <Glyph className={["h-3.5 w-3.5", step.status === "running" ? "animate-spin" : ""].join(" ")} />
          </span>
          <span className={["text-sm font-medium", s.text].join(" ")}>{s.label}</span>
          {step.action && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-600">
              {step.action}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-700" title={step.name}>
          {step.name || step.selector || "—"}
        </p>
        {step.error && (
          <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 font-mono text-[11px] text-rose-700">
            {step.error}
          </p>
        )}
      </div>
      {shotUrl && (
        <button
          type="button"
          onClick={() =>
            onOpenScreenshot({
              src: shotUrl,
              caption: `Step ${step.step_index}: ${step.name || ""}`,
            })
          }
          className="flex-none inline-flex items-center gap-1.5 self-start rounded-md border border-slate-200 bg-card px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-brand-500 hover:text-brand-700"
          title="Load screenshot from database"
        >
          <Icons.Eye className="h-3.5 w-3.5" />
          Screenshot
        </button>
      )}
    </li>
  );
}

/* A single run in the list; expands to load and show its steps. */
function RunItem({ testCaseId, run, onOpenScreenshot }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail && !loading) {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${API_BASE_URL}testcases/${testCaseId}/runs/${run.runkey}`
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        setDetail(body?.data || { steps: [] });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const dur = duration(run.started_at, run.finished_at);

  return (
    <li className="overflow-hidden rounded-xl border border-slate-200 bg-card">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <Icons.ChevronRight
          className={["h-4 w-4 flex-none text-slate-400 transition-transform", expanded ? "rotate-90" : ""].join(" ")}
        />
        <StatusBadge status={run.status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-slate-700">
            <span className="inline-flex items-center gap-1 font-medium text-slate-900">
              <Icons.Users className="h-3.5 w-3.5 text-slate-400" />
              {run.started_by_name || "Unknown"}
            </span>
            <span className="text-slate-400">·</span>
            <span className="inline-flex items-center gap-1 text-slate-500">
              <Icons.Clock className="h-3.5 w-3.5 text-slate-400" />
              {fmt(run.started_at)}
            </span>
          </div>
        </div>
        <div className="flex flex-none items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
            {run.passed} passed
          </span>
          <span className="rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">
            {run.failed} failed
          </span>
          <span className="text-slate-400">/ {run.total}</span>
          {dur && <span className="hidden text-slate-400 sm:inline">· {dur}</span>}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          {run.error && (
            <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {run.error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading steps…
            </div>
          ) : error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          ) : detail && detail.steps && detail.steps.length > 0 ? (
            <ol className="space-y-2">
              {detail.steps.map((step) => (
                <StepRow
                  key={step.step_index}
                  testCaseId={testCaseId}
                  runkey={run.runkey}
                  step={step}
                  onOpenScreenshot={onOpenScreenshot}
                />
              ))}
            </ol>
          ) : (
            <p className="py-2 text-sm text-slate-500">No step details recorded for this run.</p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Full execution history for one test case, shown as a modal.
 * Lists every recorded run (who started it, when, pass/fail counts) and lets
 * the user expand any run to inspect each step and its screenshot.
 */
export default function RunHistory({ testCase, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}testcases/${testCase.id}/runs`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setRuns(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(e.message);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [testCase.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <Icons.Clock className="h-5 w-5 text-brand-600" />
            <div>
              <h3 className="font-semibold text-slate-900">Run history</h3>
              <p className="text-xs text-slate-500">{testCase.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Icons.Refresh className={["h-3.5 w-3.5", loading ? "animate-spin" : ""].join(" ")} />
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <Icons.X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
              <Icons.Spinner className="h-5 w-5 animate-spin" />
              Loading history…
            </div>
          ) : error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : runs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">
              No runs recorded yet. Run this test case to build its history.
            </div>
          ) : (
            <ul className="space-y-2">
              {runs.map((run) => (
                <RunItem
                  key={run.runkey}
                  testCaseId={testCase.id}
                  run={run}
                  onOpenScreenshot={setLightbox}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {lightbox && (
        <ScreenshotLightbox
          src={lightbox.src}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
